import { Cores } from 'src/componentes/styles/cores'

/**
 * Contrato da API de Monitoramento dos Background Services (Observabilidade Fase 2).
 * Espelha `api/docs/api-monitor-background-services.md`. O estado (semáforo) vem
 * derivado do backend no campo `state`; a UI apenas mapeia para rótulo/cor.
 */

/** Estado derivado (semáforo) — calculado no backend. */
export enum ServiceHealthState {
  Running = 'Running',
  Ok = 'Ok',
  OkNoWork = 'OkNoWork',
  CompletedWithErrors = 'CompletedWithErrors',
  Interrupted = 'Interrupted',
  Suspect = 'Suspect',
  Failed = 'Failed',
  Stuck = 'Stuck',
  NotRun = 'NotRun'
}

/** Status cru da última execução. */
export enum BackgroundServiceExecutionStatus {
  Running = 'Running',
  Completed = 'Completed',
  CompletedWithErrors = 'CompletedWithErrors',
  Failed = 'Failed',
  Interrupted = 'Interrupted'
}

/** Resultado de um item/parcela processado. */
export enum ExecutionItemResult {
  Success = 'Success',
  Error = 'Error',
  Skipped = 'Skipped'
}

/** Tema visual de um estado de saúde — usado pelo badge dos 3 níveis. */
export interface ServiceHealthStateTema {
  label: string
  cor: string
  corFundo: string
}

/**
 * Mapa estado → rótulo (PT) + cores. Única fonte de verdade visual do semáforo;
 * a regra de derivação fica no backend (`BackgroundServiceHealthEvaluator`).
 */
export const SERVICE_HEALTH_STATE_TEMA: Record<
  ServiceHealthState,
  ServiceHealthStateTema
> = {
  [ServiceHealthState.Running]: {
    label: 'Em execução',
    cor: Cores.SECUNDARIA_1_ESCURO,
    corFundo: Cores.SECUNDARIA_1_CLARO
  },
  [ServiceHealthState.Ok]: {
    label: 'OK',
    cor: Cores.SUCESSO,
    corFundo: Cores.SUCESSO_FUNDO
  },
  [ServiceHealthState.OkNoWork]: {
    label: 'OK (sem trabalho)',
    cor: Cores.SUCESSO,
    corFundo: Cores.SUCESSO_FUNDO
  },
  [ServiceHealthState.CompletedWithErrors]: {
    label: 'Com erros',
    cor: Cores.ATENCAO_ESCURO,
    corFundo: Cores.ATENCAO_FUNDO
  },
  [ServiceHealthState.Interrupted]: {
    label: 'Interrompida (deploy/reinício)',
    cor: Cores.CINZA_2,
    corFundo: Cores.CINZA_3_CLARO
  },
  [ServiceHealthState.Suspect]: {
    label: 'Suspeito',
    cor: Cores.ATENCAO_ESCURO,
    corFundo: Cores.ATENCAO_FUNDO
  },
  [ServiceHealthState.Failed]: {
    label: 'Failed',
    cor: Cores.PERIGO_ERRO,
    corFundo: Cores.PERIGO_ERRO_FUNDO
  },
  [ServiceHealthState.Stuck]: {
    label: 'Travado (sem fim)',
    cor: Cores.CINZA_2,
    corFundo: Cores.CINZA_3_CLARO
  },
  [ServiceHealthState.NotRun]: {
    label: 'Sem execução',
    cor: Cores.CINZA_2_CLARO,
    corFundo: Cores.CINZA_3_MAIS_CLARO
  }
}

/** Tema visual por resultado de item. */
export const EXECUTION_ITEM_RESULT_TEMA: Record<
  ExecutionItemResult,
  ServiceHealthStateTema
> = {
  [ExecutionItemResult.Success]: {
    label: 'Sucesso',
    cor: Cores.SUCESSO,
    corFundo: Cores.SUCESSO_FUNDO
  },
  [ExecutionItemResult.Error]: {
    label: 'Erro',
    cor: Cores.PERIGO_ERRO,
    corFundo: Cores.PERIGO_ERRO_FUNDO
  },
  [ExecutionItemResult.Skipped]: {
    label: 'Ignorado',
    cor: Cores.CINZA_2,
    corFundo: Cores.CINZA_3_CLARO
  }
}

/** Nível 1 — uma linha por serviço esperado. */
export interface ServiceSummaryResponse {
  serviceName: string
  displayName: string
  description: string
  isFinancial: boolean
  lastExecutionAt: string | null
  state: ServiceHealthState
  status: BackgroundServiceExecutionStatus | null
  totalFound: number
  totalProcessed: number
  totalSucceeded: number
  totalFailed: number
  totalSkipped: number
  durationMs: number | null
  errorMessage: string | null
}

/** Nível 2 — uma execução. */
export interface ExecutionResponse {
  id: string
  startedAt: string
  finishedAt: string | null
  status: BackgroundServiceExecutionStatus
  state: ServiceHealthState
  totalFound: number
  totalProcessed: number
  totalSucceeded: number
  totalFailed: number
  totalSkipped: number
  durationMs: number | null
  errorMessage: string | null
  hasItems: boolean
}

/** Nível 3 — um item/parcela. */
export interface ExecutionItemResponse {
  studentName: string | null
  installmentNumber: number | null
  amount: number | null
  paymentMethod: string | null
  result: ExecutionItemResult
  errorMessage: string | null
  processedAt: string
  chargeId: string | null
  enrollmentId: string | null
  studentId: string | null
}

/**
 * Paginação legada reutilizada pela API (nomes em PT).
 * Mesmo shape de `PaginacaoPadraoDadosAPI<T>` (src/tipos/genericos.ts).
 */
export interface PageResponse<T> {
  itens: T[]
  quantidade: number
  temProximaPagina: boolean
}
